import React, { useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, ReferenceLine } from 'recharts'
import { BarChart3, Octagon, Lock, MapPin, Calendar } from 'lucide-react'
import { Vehicle } from '../types'
import { apiClient } from '../config/api'
import { DateRange, todayRange } from './CalendarModal'

interface EventChartProps {
  vehicle: Vehicle | null
  timeRange?: number
  onTimelineHover?: (timestamp: string | null, position: { lat: number, lng: number } | null) => void
  dateRange?: DateRange // Accept date range from parent
  /** Dock mode: no vehicle-identity header, tighter spacing, internal scroll */
  compact?: boolean
}


interface StopEvent {
  startTime: Date
  endTime: Date
  duration: number // in minutes
  location: string
  coordinates: { lat: number, lng: number }
  name?: string
  startTimeFormatted?: string
}

interface BaliseEvent {
  timestamp: Date
  eventType: string
  severity: string
  description: string
  location: string
}

const EventChart: React.FC<EventChartProps> = ({ vehicle, onTimelineHover, dateRange: propDateRange, compact = false }) => {
  const { t } = useTranslation()
  const [stopEvents, setStopEvents] = useState<StopEvent[]>([])
  const [baliseEvents, setBaliseEvents] = useState<BaliseEvent[]>([])
  const [loading, setLoading] = useState(false)
  const [hoveredStop, setHoveredStop] = useState<StopEvent | null>(null)
  
  // Use date range from props or default
  const dateRange = propDateRange || todayRange()

  useEffect(() => {
    if (vehicle && dateRange.startDate && dateRange.endDate) {
      // A rolling window carries exact instants; whole-day presets do not.
      fetchEventData(
        vehicle.id,
        dateRange.startAt || dateRange.startDate,
        dateRange.endAt || dateRange.endDate
      )
    } else {
      setStopEvents([])
      setBaliseEvents([])
    }
  }, [vehicle, dateRange])


  const fetchEventData = async (vehicleId: string, start: string, end: string) => {
    setLoading(true)
    try {
      console.log(`📊 Fetching event data for ${vehicleId} from ${start} to ${end}`)
      
      // Calculate hours from date range
      const startTime = new Date(start).getTime()
      const endTime = new Date(end).getTime()
      const hours = Math.ceil((endTime - startTime) / (1000 * 60 * 60))
      
      // Fetch timeline data using full date range
      console.log(`⏰ Requesting ${hours} hours of event data for full date range`)
      
      const response = await apiClient.get(`/timeline/${vehicleId}`, { 
        startDate: start,
        endDate: end,
        hours: hours
      })
      
      if (response.data && Array.isArray(response.data)) {
        console.log(`📊 EVENT DATA RECEIVED:`, response.data.length, 'events')
        
        const events = response.data.map((event: any) => ({
          time: new Date(event.timestamp).toISOString(),
          eventType: event.event || 'GPS_UPDATE',
          duration: 0,
          location: `${event.location.latitude.toFixed(4)}, ${event.location.longitude.toFixed(4)}`,
          timestamp: new Date(event.timestamp),
          severity: event.severity || 'info',
          speed: event.speed || 0,
          batteryLevel: event.batteryLevel || 0
        }))
        
        // Process stop events (stops > 10 minutes)
        const stops = detectStopEvents(events)
        console.log(`🛑 Detected ${stops.length} stop events > 10 minutes`)
        
        // Process balise events (open/close/access events)
        const baliseEvts = detectBaliseEvents(events)
        console.log(`🔒 Detected ${baliseEvts.length} balise events`)
        
        setStopEvents(stops)
        setBaliseEvents(baliseEvts)
        
      } else {
        console.log('📊 No event data available')
        setStopEvents([])
        setBaliseEvents([])
      }
      
    } catch (error) {
      console.error('❌ Error fetching event data:', error)
      setStopEvents([])
      setBaliseEvents([])
    } finally {
      setLoading(false)
    }
  }

  // Handle mouse move over stop chart
  const handleStopMouseMove = useCallback((data: any) => {
    if (data && data.activePayload && data.activePayload[0]) {
      const stopData = data.activePayload[0].payload
      const stop = stopEvents.find(s => s.name === stopData.name)
      if (stop) {
        setHoveredStop(stop)
        // Notify parent component with position for map interaction
        if (onTimelineHover && stop.coordinates) {
          onTimelineHover(stop.startTimeFormatted || stop.startTime.toLocaleTimeString(), stop.coordinates)
        }
      }
    }
  }, [onTimelineHover, stopEvents])

  // Handle mouse leave stop chart
  const handleStopMouseLeave = useCallback(() => {
    setHoveredStop(null)
    if (onTimelineHover) {
      onTimelineHover(null, null)
    }
  }, [onTimelineHover])

  // Detect stop events longer than 10 minutes
  const detectStopEvents = (events: any[]): StopEvent[] => {
    const stops: StopEvent[] = []
    let stopStart: any = null
    
    for (let i = 0; i < events.length; i++) {
      const event = events[i]
      const isStationary = event.speed < 1 // Speed < 1 km/h considered stopped
      
      if (isStationary && !stopStart) {
        // Start of stop event
        stopStart = event
      } else if (!isStationary && stopStart) {
        // End of stop event
        const duration = (event.timestamp.getTime() - stopStart.timestamp.getTime()) / (1000 * 60) // minutes
        
        if (duration >= 10) { // Only stops > 10 minutes
          stops.push({
            startTime: stopStart.timestamp,
            endTime: event.timestamp,
            duration: Math.round(duration),
            location: stopStart.location,
            coordinates: {
              lat: parseFloat(stopStart.location.split(',')[0]),
              lng: parseFloat(stopStart.location.split(',')[1])
            },
            name: `Stop ${stops.length + 1}`,
            startTimeFormatted: stopStart.timestamp.toLocaleTimeString()
          })
        }
        stopStart = null
      }
    }
    
    return stops
  }

  // Detect balise events (access, open, close, etc.)
  const detectBaliseEvents = (events: any[]): BaliseEvent[] => {
    return events
      .filter(event => 
        event.eventType && 
        (event.eventType.includes('ACCESS') || 
         event.eventType.includes('CARD') ||
         event.eventType.includes('OPEN') ||
         event.eventType.includes('CLOSE') ||
         event.eventType.includes('LOCK') ||
         event.eventType.includes('UNLOCK') ||
         event.severity === 'high' ||
         event.severity === 'critical')
      )
      .map(event => ({
        timestamp: event.timestamp,
        eventType: event.eventType,
        severity: event.severity,
        description: `${event.eventType} event`,
        location: event.location
      }))
  }

  // Prepare chart data for stop events
  const stopChartData = stopEvents.map((stop, index) => ({
    name: `Stop ${index + 1}`,
    duration: stop.duration,
    startTime: stop.startTime.toLocaleTimeString(),
    location: stop.location
  }))

  // Prepare chart data for event types
  const eventTypeData = baliseEvents.reduce((acc: any[], event) => {
    const existing = acc.find(item => item.name === event.eventType)
    if (existing) {
      existing.value++
    } else {
      acc.push({ name: event.eventType, value: 1 })
    }
    return acc
  }, [])

  // Colors for pie chart
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82CA9D']

  if (!vehicle) {
    return (
      <div className={`h-full bg-white flex items-center justify-center ${compact ? '' : 'border-t'}`}>
        <div className="text-center text-gray-500">
          <BarChart3 size={22} className="mx-auto mb-2 text-gray-400" />
          <div className="text-lg mb-1">{t('event_analysis')}</div>
          <div className="text-sm">{t('events_select_hint')}</div>
        </div>
      </div>
    )
  }

  return (
    <div className={`h-full bg-white flex flex-col min-h-0 ${compact ? '' : 'border-t'}`}>
      {/* Header: full vehicle identity only outside compact/dock mode */}
      {!compact && (
        <div className="px-3 py-2 border-b bg-gray-50 shrink-0">
          <div className="flex items-center justify-between mb-2">
            <div>
              <h3 className="font-semibold text-gray-800">{vehicle.name}</h3>
              <div className="text-sm text-gray-600">
                {t('event_analysis')}
              </div>
              <div className="text-xs text-gray-500">ID: {vehicle.plateNumber}</div>
            </div>
          </div>

          {/* Date range controlled by parent component */}
          <div className="text-xs text-gray-500">
            {t('period_label')}: {dateRange.label || `${new Date(dateRange.startDate).toLocaleDateString('en-GB')} - ${new Date(dateRange.endDate).toLocaleDateString('en-GB')}`}
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-2 text-gray-600">{t('loading')}</span>
        </div>
      ) : (
        <div className={`flex-1 min-h-0 overflow-y-auto ${compact ? 'p-2 space-y-3' : 'p-3 space-y-4'}`}>
          {/* Stop Events Chart */}
          <div>
            <h4 className={`flex items-center gap-1.5 font-medium text-gray-800 ${compact ? 'mb-2 text-xs' : 'mb-3'}`}>
              <Octagon size={compact ? 12 : 14} className="text-red-500" />
              {t('stop_events_title')} ({stopEvents.length})
            </h4>
            {stopChartData.length > 0 ? (
              <div className={compact ? 'h-36' : 'h-48'}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart 
                    data={stopChartData}
                    onMouseMove={handleStopMouseMove}
                    onMouseLeave={handleStopMouseLeave}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" tick={{ fontSize: compact ? 10 : 12 }} />
                    <YAxis
                      tick={{ fontSize: compact ? 10 : 12 }}
                      width={compact ? 30 : undefined}
                      label={compact ? undefined : { value: t('duration_min'), angle: -90, position: 'insideLeft' }}
                    />
                    <Tooltip 
                      formatter={(value: any, _name: string, props: any) => {
                        const stopData = props.payload
                        const locationText = stopData.location ? ` | ${stopData.location}` : ''
                        return [`${value} min${locationText}`, t('stop_duration')]
                      }}
                      labelFormatter={(label: string) => {
                        const stop = stopChartData.find(s => s.name === label)
                        return stop ? `${label} — ${stop.startTime}` : label
                      }}
                    />
                    <Bar dataKey="duration" fill="#ef4444" />
                    {/* Hover indicator line */}
                    {hoveredStop && (
                      <ReferenceLine 
                        x={hoveredStop.name} 
                        stroke="#ff6b6b" 
                        strokeWidth={2}
                        strokeDasharray="2 2"
                      />
                    )}
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className={`text-center text-gray-500 ${compact ? 'py-4 text-xs' : 'py-8'}`}>
                {t('no_stop_events')}
              </div>
            )}
          </div>

          {/* Balise Events Chart */}
          <div>
            <h4 className={`flex items-center gap-1.5 font-medium text-gray-800 ${compact ? 'mb-2 text-xs' : 'mb-3'}`}>
              <Lock size={compact ? 12 : 14} className="text-blue-500" />
              {t('balise_events_title')} ({baliseEvents.length})
            </h4>
            {eventTypeData.length > 0 ? (
              <div className="flex space-x-4">
                {/* Pie Chart */}
                <div className={`flex-1 ${compact ? 'h-36' : 'h-48'}`}>
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={eventTypeData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                        outerRadius={compact ? 52 : 80}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {eventTypeData.map((_entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Event List */}
                <div className="flex-1 min-w-0">
                  <div className={`font-medium text-gray-700 mb-2 ${compact ? 'text-xs' : 'text-sm'}`}>{t('recent_events')}</div>
                  <div className={`overflow-y-auto space-y-1 ${compact ? 'max-h-36' : 'max-h-48'}`}>
                    {baliseEvents.slice(0, 10).map((event, index) => (
                      <div key={index} className="bg-gray-50 rounded p-2">
                        <div className="flex items-center justify-between">
                          <span className={`text-xs px-2 py-1 rounded ${
                            event.severity === 'critical' ? 'bg-red-100 text-red-800' :
                            event.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                            event.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-blue-100 text-blue-800'
                          }`}>
                            {event.eventType}
                          </span>
                          <span className="text-xs text-gray-500">
                            {event.timestamp.toLocaleTimeString()}
                          </span>
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          {event.description}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className={`text-center text-gray-500 ${compact ? 'py-4 text-xs' : 'py-8'}`}>
                {t('no_balise_events')}
              </div>
            )}
          </div>

          {/* Summary Statistics */}
          <div className={`grid grid-cols-3 ${compact ? 'gap-2' : 'gap-4'}`}>
            <div className={`bg-blue-50 rounded-lg text-center ${compact ? 'p-2' : 'p-4'}`}>
              <div className={`font-bold text-blue-600 ${compact ? 'text-base' : 'text-2xl'}`}>{stopEvents.length}</div>
              <div className={`text-blue-800 ${compact ? 'text-xs' : 'text-sm'}`}>{t('long_stops')}</div>
              <div className="text-xs text-blue-600">&gt; 10 min</div>
            </div>
            <div className={`bg-green-50 rounded-lg text-center ${compact ? 'p-2' : 'p-4'}`}>
              <div className={`font-bold text-green-600 ${compact ? 'text-base' : 'text-2xl'}`}>{baliseEvents.length}</div>
              <div className={`text-green-800 ${compact ? 'text-xs' : 'text-sm'}`}>{t('balise_events_title')}</div>
              <div className="text-xs text-green-600">{t('access_lock_events')}</div>
            </div>
            <div className={`bg-purple-50 rounded-lg text-center ${compact ? 'p-2' : 'p-4'}`}>
              <div className={`font-bold text-purple-600 ${compact ? 'text-base' : 'text-2xl'}`}>
                {stopEvents.reduce((total, stop) => total + stop.duration, 0)}
              </div>
              <div className={`text-purple-800 ${compact ? 'text-xs' : 'text-sm'}`}>{t('total_stop_time')}</div>
              <div className="text-xs text-purple-600">{t('minutes')}</div>
            </div>
          </div>

          {/* Detailed Stop Events List */}
          {stopEvents.length > 0 && (
            <div>
              <h4 className={`flex items-center gap-1.5 font-medium text-gray-800 ${compact ? 'mb-2 text-xs' : 'mb-3'}`}>
                <MapPin size={compact ? 12 : 14} className="text-purple-500" />
                {t('detailed_stops')}
              </h4>
              <div className={`space-y-2 overflow-y-auto ${compact ? 'max-h-36' : 'max-h-48'}`}>
                {stopEvents.map((stop, index) => (
                  <div key={index} className={`bg-gray-50 rounded-lg ${compact ? 'p-2' : 'p-3'}`}>
                    <div className="flex items-center justify-between mb-2">
                      <span className={`font-medium text-gray-800 ${compact ? 'text-xs' : ''}`}>
                        Stop #{index + 1}
                      </span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        stop.duration > 60 ? 'bg-red-100 text-red-800' :
                        stop.duration > 30 ? 'bg-orange-100 text-orange-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {stop.duration} min
                      </span>
                    </div>
                    <div className={`text-gray-600 space-y-1 ${compact ? 'text-xs' : 'text-sm'}`}>
                      <div className="flex items-center gap-1.5">
                        <Calendar size={12} className="shrink-0 text-gray-400" />
                        {stop.startTime.toLocaleString()} → {stop.endTime.toLocaleString()}
                      </div>
                      <div className="flex items-center gap-1.5">
                        <MapPin size={12} className="shrink-0 text-gray-400" />
                        {stop.location}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default EventChart
